import React, { useEffect } from 'react';
import type { DefaultValues, FieldValues } from 'react-hook-form';
import * as yup from 'yup';
import {
	type FormDefinition,
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import { useFormStore } from '@gov-nx/store';
import type {
	AutocompleteDefinition,
	BadgeListDefinition,
	CheckboxDefinition,
	DateDefinition,
	FileUploadDefinition,
	InputDefinition,
	MultiselectDefinition,
	PhoneDefinition,
	RadioDefinition,
	SelectDefinition,
} from '@gov-nx/utils/common';
import type { StreetNumbersFormDefinitions } from '@gov-nx/core/hooks';
import { useAuthorizationConsentDefinition } from '@gov-nx/core/hooks';

export interface FormDefinition2<
	T extends FieldValues,
	Fields,
> extends FormDefinition<T> {
	fields: Fields;
}

type Field =
	| InputDefinition
	| BadgeListDefinition
	| RadioDefinition
	| DateDefinition
	| CheckboxDefinition
	| SelectDefinition
	| PhoneDefinition
	| AutocompleteDefinition
	| MultiselectDefinition
	| FileUploadDefinition
	| StreetNumbersFormDefinitions;

/**
 * This is new form builder which should be used for building all the forms, It is not perfect yet but it is a start
 */
export const useFormBuilder = <Form extends FieldValues, Fields>(
	definitions: Field[]
): FormDefinition2<Form, Fields> => {
	const schema = definitions.reduce(
		(all, field) => ({ ...all, [field.field.field.name]: field.schema }),
		{} as Record<string, yup.AnySchema>
	);

	const formSchema = yup.object(schema).required() as yup.ObjectSchema<Form>;

	const defaultValues = definitions.reduce(
		(all, field) => ({ ...all, [field.field.field.name]: field.defaultValue }),
		{} as DefaultValues<Form>
	);

	const formMethods = useGovForm<Form>({
		formSchema,
		defaultValues,
	});

	const formDefinition = getFormDefinition<Form>({ formMethods, formSchema });

	const fields = definitions.reduce(
		(all, field) => ({ ...all, [field.field.field.name]: field.field }),
		{} as Fields
	);

	return {
		...formDefinition,
		fields,
	};
};

export const useFormBuilderWithConsent = <Form extends FieldValues, Fields>(
	definitions: Field[],
	options: { isLowAuthorization: boolean; isDisabled: boolean }
): FormDefinition2<Form, Fields> => {
	// Always call the hook unconditionally to satisfy React's Rules of Hooks,
	// but only include the field in the form when consent is actually required.
	// This prevents __souhlas from polluting form state (and default payloads)
	// for flows where authorization consent is not needed.
	const consentDefinition = useAuthorizationConsentDefinition({
		isDisabled: options.isDisabled,
		isConsentRequired: options.isLowAuthorization,
	});

	const allDefinitions = options.isLowAuthorization
		? [...definitions, consentDefinition]
		: definitions;

	return useFormBuilder<Form, Fields>(allDefinitions);
};

export const usePersistedForm = (persistKey: string) => {
	const storeUpdate = useFormStore('update');
	const storeReset = useFormStore('reset');
	const storeValues = useFormStore(persistKey);

	return function usePersistedForm<Form extends FieldValues, Fields>(
		formDefinition: FormDefinition2<Form, Fields>
	): FormDefinition2<Form, Fields> {
		React.useEffect(() => {
			const subscription = formDefinition.formMethods.watch((value) => {
				if (storeValues !== value) {
					storeUpdate(persistKey, value);
				}
			});
			return () => subscription.unsubscribe();
		}, [formDefinition.formMethods]);

		// Set default values from store if they exist
		useEffect(() => {
			if (storeValues) {
				formDefinition.formMethods.reset(storeValues as Form);
			}
		}, [formDefinition.formMethods]);

		return {
			...formDefinition,
			formReset: () => {
				// override formReset to also reset store
				formDefinition.formReset();
				storeReset(persistKey);
			},
		};
	};
};
